import type { IconSize } from '@components/v2/types';
import Icon from '@components/v2/ui/Icon/Icon';
import { IconButton as MuiIconButton, IconButtonProps as MuiIconButtonProps } from '@mui/material';
import { ElementType, forwardRef } from 'react';
import { toLinkProps } from '../utils/toLinkProps';

const ICON_SIZE_MAP: Record<'small' | 'medium' | 'large', IconSize> = {
  small: 'xs',
  medium: 'md',
  large: 'lg',
};

type Props = Pick<
  MuiIconButtonProps,
  'onClick' | 'onContextMenu' | 'disabled' | 'children' | 'className' | 'sx' | 'shape' | 'tabIndex'
> & {
  icon: ElementType;
  size?: 'small' | 'medium' | 'large';
  color?: 'primary' | 'secondary' | 'accent';
  isActive?: boolean;
  testId?: string;
  ariaLabel?: string;
  ariaExpanded?: boolean;
  ariaHasPopup?: boolean;
  ariaPressed?: boolean;
  to?: string;
};

const IconButton = forwardRef<HTMLButtonElement, Props>(
  (
    {
      icon,
      size = 'medium',
      color = 'primary',
      shape,
      isActive,
      onClick,
      onContextMenu,
      disabled,
      children,
      className,
      sx,
      testId,
      ariaLabel,
      ariaExpanded,
      ariaHasPopup,
      ariaPressed,
      tabIndex,
      to,
    },
    ref,
  ) => (
    <MuiIconButton
      size={size}
      color={color}
      shape={shape}
      onClick={onClick}
      onContextMenu={onContextMenu}
      disabled={disabled}
      className={className}
      test-id={testId}
      aria-label={ariaLabel}
      aria-expanded={ariaExpanded}
      aria-haspopup={ariaHasPopup}
      aria-pressed={ariaPressed}
      data-active={isActive || undefined}
      tabIndex={tabIndex}
      ref={ref}
      sx={sx}
      {...toLinkProps(to)}
    >
      <Icon icon={icon} size={ICON_SIZE_MAP[size]} />
      {children}
    </MuiIconButton>
  ),
);

IconButton.displayName = 'IconButton';
export type { Props as IconButtonProps };
export default IconButton;
